<script setup lang="ts">
import {computed, onMounted, ref, watch} from "vue";
import {useCrossFilterStore} from "@/stores/crossFilterStore.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import QuestionFieldsEditor from "@/components/questionFields/QuestionFieldsEditor.vue";
import CrossFilterChip from "@/components/bi/CrossFilterChip.vue";
import Translator from "@/utils/common/Translator.ts";
import QuestionField = namaTypes.QuestionField;

const props = defineProps<{
  questionFields: QuestionField[]
}>();

const emit = defineEmits<{
  filtersChanged: []
}>();

const crossFilterStore = useCrossFilterStore();
const presenter = new QuestionFieldsPresenter();
const dialogOpen = ref(false);

onMounted(async () => {
  await initStore();
});

watch(() => props.questionFields, async () => {
  await initStore();
}, {immediate: true});

async function initStore() {
  if (!props.questionFields?.length) return;
  crossFilterStore.init(props.questionFields);
}

async function openDialog() {
  // Reset before reseeding — `presenter.edit()` only writes fields that have a default value, so
  // values from a previous open would otherwise stick around even after their chips were removed.
  presenter.record.value = {};
  await presenter.edit(props.questionFields, crossFilterStore.getGlobalFilledQuestionFields());
  dialogOpen.value = true;
}

function onApplyFilters() {
  const filled = presenter.getQuestionFields();
  crossFilterStore.clearGlobalFilters();
  for (const f of filled) {
    if (f.id && f.value?.trim())
      crossFilterStore.setFilter(f.id, f.value);
  }
  dialogOpen.value = false;
  emit('filtersChanged');
}

const activeCount = computed(() => crossFilterStore.filledQuestions.size);
</script>

<template>
  <div v-if="questionFields?.length || $slots.actions"
       class="tw-w-full tw-border-b tw-border-gray-200 dark:tw-border-gray-600 tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark tw-px-3 tw-py-2 tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
    <slot name="actions"/>
    <q-btn v-if="questionFields?.length" dense outline color="primary" icon="filter_list"
           :label="Translator.translate('biFilters')" @click="openDialog">
      <q-badge v-if="activeCount > 0" floating color="red">{{ activeCount }}</q-badge>
    </q-btn>

    <CrossFilterChip v-for="[id, filter] in crossFilterStore.filledQuestions" :key="id"
                     :filledQuestion="filter"
                     :questionField="crossFilterStore.findQuestionField(id)"
                     @remove="crossFilterStore.clearFilter(id); emit('filtersChanged')"/>

    <q-dialog v-model="dialogOpen">
      <q-card class="tw-flex tw-flex-col tw-bg-namaBackground-bright-light dark:tw-text-white dark:tw-bg-namaBackground-bright-dark"
              style="min-width: 500px;">
        <q-bar class="tw-text-namaFontColor-input-light tw-bg-[#CED4DA] tw-px-2 tw-py-[6px] tw-flex tw-justify-between dark:tw-bg-[#212529] dark:tw-text-white">
          <div>{{ Translator.translate('biCrossFilters') }}</div>
          <q-btn dense flat icon="close" v-close-popup :title="Translator.translate('biClose')"/>
        </q-bar>
        <q-card-section>
          <QuestionFieldsEditor :presenter="presenter" :singleColumnGroup="true" :doNotApplyThemeLayouts="true"/>
        </q-card-section>
        <q-card-actions align="right" class="tw-px-3 tw-pb-3">
          <q-btn flat :label="Translator.translate('biCancel')" v-close-popup/>
          <q-btn color="primary" :label="Translator.translate('biApply')" @click="onApplyFilters"/>
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>
